import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectAccessToken } from "../../authentication/authentication-slice";
import { useEffect, useState } from "react";
import { fetchSeries, Period, selectChosenAvg, selectChosenMarketData, selectChosenMax, selectChosenMin, selectChosenSeries, selectFormatMap, selectPeriod, selectStatus, selectSymbol, setPeriod } from "./stock-data-slice";
import LineChart, { DataPoint } from "../../../commonComponents/chart/lineChart/line-chart";
import { plainToInstance } from "class-transformer";
import { MultipleValue, ValuationMultiple } from "../../../api/models/valuation/valuationMultiple";
import SwitchButton, { DISPLAY_OPTIONS } from "../../../commonComponents/switchButton/switch-button";
import { FaQuestionCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import { FormatValue, isMobile } from "../../../app/utils";

/**
 * Line Chart for showing stock data react component.
 */
const StockDataChart: React.FC = () => {

  // React states
  const [zScoreEnabled, setZScoreEnabled] = useState(false);
  const [lineChartSeries, setLineChartSeries] = useState<DataPoint[]>([]);

  // Redux data
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken);
  const period = useAppSelector(selectPeriod);
  const chosenMarketData = useAppSelector(selectChosenMarketData)
  const chosenSeries = useAppSelector(selectChosenSeries);
  const chosenMin = plainToInstance(MultipleValue, useAppSelector(selectChosenMin));
  const chosenAvg = useAppSelector(selectChosenAvg);
  const chosenMax = plainToInstance(MultipleValue, useAppSelector(selectChosenMax));
  const valueFormat = useAppSelector(selectFormatMap).get(chosenMarketData) ?? "percentage";
  const status = useAppSelector(selectStatus)[chosenMarketData];

  // Change chart series when the chosen series changes
  useEffect(() => {
    const multiples = plainToInstance(ValuationMultiple, chosenSeries);
    setLineChartSeries(multiples.series.map(multiple => { return { x: multiple.date, y: multiple.value, label: multiple.description } }));
  }, [chosenSeries])

  // When period changes, fetch the data again
  function onPeriodChange(period: Period): void {
    dispatch(setPeriod(period));
    if (accessToken) {
      dispatch(fetchSeries({ accessToken, marketData: chosenMarketData, period }));
    }
  }

  return (

    <div className="relative flex flex-col w-full h-full rounded-md overflow-hidden p-1 lg:p-10 mb-5 select-none lg:bg-black/10 lg:border-2 lg:border-blue-300/20 lg:shadow-2xl lg:shadow-slate-900/80 transition-opacity duration-1000">

      {/** div used for loading time UX improvement */}
      <div className={`absolute z-50 top-0 left-0 w-full h-full text-opacity-0
        ${status === "pending" ? "animate-loading-bg" : "hidden"}`}>
      </div>

      <div className="flex flex-row items-center">

        {/** Switch buttons: Z-Score and Inflation Adjustment */}
        <div className="grow lg:ml-2">
          <div className={`flex flex-row w-full justify-start transition-opacity duration-1000 ${lineChartSeries.length === 0 ? " opacity-0 " : " opacity-100 "}`}>
            {/** Z-Score */}
            <div className="flex">
              <SwitchButton optionA="" optionB="Z-score" onOptionChange={(option) => { setZScoreEnabled(option === DISPLAY_OPTIONS.OPTION_B) }} />
              <FaQuestionCircle data-tooltip-id="tooltipZScore" className="self-start ml-2 mt-0.5 rounded-full shadow-md shadow-slate-900/80" color="rgba(255,255,255,0.3)" size={isMobile() ? 20 : 25} />
              <Tooltip place="top" id="tooltipZScore" style={{ backgroundColor: "rgba(0, 0, 0, 0.95)", zIndex: 50 }} >
                <div className="flex flex-col items-center justify-center text-xs lg:text-sm font-semibold text-neutral-300">
                  <label>O z-score mostra o quanto um valor se afasta</label>
                  <label>da média, medido em unidades de desvio-padrão.<br /><br /></label>

                  <label>Por exemplo, um z-score de 2 indica que o</label>
                  <label>valor está dois desvios-padrão acima da média,</label>
                  <label>ous seja, bem mais alto que a maioria.<br /><br /></label>

                  <label className="hidden lg:flex">Esses desvios têm relação com percentuais de confiança:</label>
                  <label className="hidden lg:flex">cerca de 68% dos dados estão até 1 desvio da média,</label>
                  <label className="hidden lg:flex">95% até 2 desvios e 99,7% até 3.<br /><br /></label>
                  <label className="hidden lg:flex">Então, quanto mais distante do zero o z-score está,</label>
                  <label className="hidden lg:flex">mais raro e fora do padrão é o valor.</label>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>

        {/** Period selection */}
        <div className="relative flex flex-row justify-end text-xs lg:text-sm *:text-center lg:*:w-20 *:w-10 *:py-0.5 *:px-2 *:ml-2 lg:*:ml-4 *:rounded-md *:border *:cursor-pointer">
          <div className={`absolute h-full left-0 transition-transform duration-150 text-white/0 bg-slate-900/50 border-blue-100/40 shadow-md shadow-slate-900/80
                ${period === "1A" ? "translate-x-0"
              : period === "5A" ? "translate-x-12 lg:translate-x-24"
                : period === "10A" ? "translate-x-24 lg:translate-x-48"
                  : "translate-x-36 lg:translate-x-72"}`}>
            <div className="w-10" />
          </div>
          <div onClick={() => onPeriodChange("1A")} className="relative border-neutral-200/0">
            <p className="lg:hidden">1A</p>
            <p className="hidden lg:block">1 Ano</p>
          </div>
          <div onClick={() => onPeriodChange("5A")} className="relative border-neutral-200/0">
            <p className="lg:hidden">5A</p>
            <p className="hidden lg:block">5 Anos</p>
          </div>
          <div onClick={() => onPeriodChange("10A")} className="relative border-neutral-200/0">
            <p className="lg:hidden">10A</p>
            <p className="hidden lg:block">10 Anos</p>
          </div>
          <div onClick={() => onPeriodChange("T")} className="relative border-neutral-200/0">
            <p className="lg:hidden">T</p>
            <p className="hidden lg:block">Tudo</p>
          </div>
        </div>
      </div>

      {/** The chart itself */}
      <div className="grow mt-2">
        <LineChart originalData={lineChartSeries} yFormat={valueFormat} zScoreEnabled={zScoreEnabled} showMinMaxValues={true} />
      </div>

      {/** Maximum, average and minimum values */}
      <div className="flex flex-row w-full mt-7 lg:mt-10 justify-around lg:justify-evenly">

        {/** MIN VALUE */}
        <div className="flex flex-col items-center">
          <p className="text-lg leading-none text-red-300">MENOR</p>
          <p className="text-2xl">{FormatValue(chosenMin.value, valueFormat)}</p>
          <p className="text-xs lg:text-sm">em {chosenMin?.date?.toLocaleDateString()}</p>
        </div>

        {/** AVG VALUE */}
        <div className="flex flex-col items-center">
          <p className="text-lg leading-none text-blue-300">MÉDIA</p>
          <p className="text-2xl">{FormatValue(chosenAvg, valueFormat)}</p>
        </div>

        {/** MAX VALUE */}
        <div className="flex flex-col items-center">
          <p className="text-lg leading-none text-green-300">MAIOR</p>
          <p className="text-2xl">{FormatValue(chosenMax.value, valueFormat)}</p>
          <p className="text-xs lg:text-sm">em {chosenMax?.date?.toLocaleDateString()}</p>
        </div>

      </div>

    </div>
  )
}

export default StockDataChart;